/**
 * Waiting for the facilitator to let you in.
 *
 * Reached from "Try another way" on the code screen. The request sits in the
 * console's "Waiting for access" list; this screen asks the server every few
 * seconds how it stands, and the poll that finds it approved is the one that
 * signs this browser in. From there the player goes to onboarding as if their
 * code had worked.
 *
 * A denial is said kindly and is not the end. The usual reasons are ordinary:
 * the facilitator did not recognise the name, or pressed the wrong button. So
 * the screen offers to ask again, and it keeps listening while it waits, so a
 * facilitator who corrects a mis-click lets the player straight in.
 *
 * Polling pauses while the tab is hidden and resumes, immediately, when it is
 * shown again. A phone in a pocket should not be spending the venue's Wi-Fi on
 * an answer nobody is looking at, and one taken out of a pocket should not wait
 * a beat to show the answer that arrived while it was away.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiRequestError, type AccessRequestState } from "../api.js";
import { fetchAuthState } from "../auth/authApi.js";

const POLL_MS = 3000;

export type ApprovedAccess = Extract<AccessRequestState, { status: "approved" }>;

export interface AccessRequestScreenProps {
  token: string;
  email: string;
  name: string;
  sessionCode: string;
  sessionTitle: string;
  /** The browser is now signed in; go on to onboarding. */
  onApproved: (approved: ApprovedAccess) => void;
  /** Put the request back in the queue. Rejects with a message to show. */
  onAskAgain: () => Promise<void>;
  /** Back to the six boxes, for someone whose code turned up after all. */
  onUseCode: () => void;
  /** The request is gone for good; start from the room code. */
  onStartOver: () => void;
}

type View = { kind: "pending" } | { kind: "denied" } | { kind: "gone"; message: string };

export function AccessRequestScreen({
  token,
  email,
  name,
  sessionCode,
  sessionTitle,
  onApproved,
  onAskAgain,
  onUseCode,
  onStartOver,
}: AccessRequestScreenProps): React.ReactElement {
  const [view, setView] = useState<View>({ kind: "pending" });
  const [asking, setAsking] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const finished = useRef(false);
  const inFlight = useRef(false);
  // Read through a ref so a parent passing a fresh closure on every render does
  // not restart the polling loop, and fire an immediate extra poll, each time.
  const approvedRef = useRef(onApproved);
  approvedRef.current = onApproved;
  // "Ask again" replaces the token. A poll sent with the old one can still be
  // in flight, and its answer — "no longer active" — is about a request this
  // screen has already left behind. Answers are only believed for the token
  // that is current when they arrive.
  const tokenRef = useRef(token);
  tokenRef.current = token;

  const check = useCallback(async () => {
    if (finished.current || inFlight.current) return;
    inFlight.current = true;
    const stale = () => tokenRef.current !== token;
    try {
      const state = await api.accessRequestStatus(token);
      if (finished.current || stale()) return;
      if (state.status === "approved") {
        finished.current = true;
        approvedRef.current(state);
        return;
      }
      setView({ kind: state.status });
    } catch (cause) {
      if (finished.current || stale()) return;
      if (!(cause instanceof ApiRequestError)) return; // offline for a moment; try again next tick

      // The approval was collected but the response never arrived — a dropped
      // connection at exactly the wrong moment. The cookie may still have
      // landed, and if it did this person is signed in and should go on.
      const auth = await fetchAuthState();
      if (auth.signedIn && auth.email?.toLowerCase() === email.toLowerCase()) {
        finished.current = true;
        approvedRef.current({
          status: "approved",
          email,
          suggestedName: name,
          sessionCode,
          sessionTitle,
        });
        return;
      }
      finished.current = true;
      setView({ kind: "gone", message: cause.message });
    } finally {
      inFlight.current = false;
    }
  }, [token, email, name, sessionCode, sessionTitle]);

  useEffect(() => {
    finished.current = false;
    void check();
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") void check();
    }, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") void check();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [check]);

  const askAgain = async () => {
    setAsking(true);
    setNotice(null);
    try {
      await onAskAgain();
      setView({ kind: "pending" });
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : "That didn't go through. Try again.");
    } finally {
      setAsking(false);
    }
  };

  const linkStyle: React.CSSProperties = {
    background: "none",
    border: "none",
    color: "inherit",
    opacity: 0.85,
    textDecoration: "underline",
    fontSize: 15,
    cursor: "pointer",
    padding: 8,
  };

  return (
    <div className="tq-player">
      <div className="tq-player-main tq-phone-card" aria-live="polite">
        {view.kind === "pending" ? (
          <>
            <div className="tq-phone-idle">
              <div className="tq-phone-idle-icon" aria-hidden="true">
                🙋
              </div>
              <p className="tq-phone-idle-text">
                <b>Waiting for the facilitator</b>
                <br />
                You&apos;ve asked to join <strong>{sessionTitle}</strong> as <strong>{name}</strong>{" "}
                ({email}). Keep this screen open. You&apos;ll go straight in as soon as they let you
                in.
              </p>
            </div>
            <p style={{ textAlign: "center", margin: 0 }}>
              <button type="button" style={linkStyle} onClick={onUseCode}>
                My code arrived. Use it instead.
              </button>
            </p>
          </>
        ) : view.kind === "denied" ? (
          <>
            <div className="tq-phone-idle">
              <div className="tq-phone-idle-icon" aria-hidden="true">
                🤝
              </div>
              <p className="tq-phone-idle-text">
                <b>The facilitator couldn&apos;t let you in this way</b>
                <br />
                No worries. This usually just means they didn&apos;t recognize the name. Have a
                quick word with them, then ask again. Or use the code we emailed you.
              </p>
            </div>
            <div className="tq-phone-hint" role="alert">
              {notice}
            </div>
            <button
              type="button"
              className="tq-phone-primary"
              disabled={asking}
              onClick={() => void askAgain()}
              style={{ minHeight: 52, width: "100%" }}
            >
              {asking ? "Asking…" : "Ask again"}
            </button>
            <p style={{ textAlign: "center", margin: "8px 0 0" }}>
              <button type="button" style={linkStyle} onClick={onUseCode}>
                Use the emailed code
              </button>
            </p>
          </>
        ) : (
          <>
            <div className="tq-phone-idle">
              <div className="tq-phone-idle-icon" aria-hidden="true">
                ✋
              </div>
              <p className="tq-phone-idle-text">
                <b>This request has closed</b>
                <br />
                {view.message}
              </p>
            </div>
            <button
              type="button"
              className="tq-phone-primary"
              onClick={onStartOver}
              style={{ minHeight: 52, width: "100%" }}
            >
              Start over
            </button>
          </>
        )}
      </div>
    </div>
  );
}
